/**
 * Opsec — wiring.
 *
 * This is a live board, not an archive. There is no scrubber and no playback:
 * the map shows the trailing 24 hours and refreshes itself. Everything older
 * than a day is simply not drawn.
 */

import { MapboxOverlay } from "@deck.gl/mapbox";
import { AmbientLight, LightingEffect, DirectionalLight } from "@deck.gl/core";

import {
  archiveExists, createMap, locateViewer, watchTaps, watchTheme,
} from "./map/basemap.js";
import { recordClick, recordHover } from "./map/picking.js";
import { initTheme, onThemeChange } from "./theme/palette.js";
import {
  buildingLayer, buildingTileLayer, loadBuildings, makeGroundHeight,
  makeIsKnown, makeTintAt, toPolygons,
} from "./map/buildings.js";
import { footprints } from "./map/footprints.js";
import { chunk, runSliced } from "./lib/slice.js";
import { PlaceIndex } from "./map/places.js";
import { watchForShaderErrors } from "./map/shader-guard.js";
import { IncidentLayer } from "./layers/incidents.js";
import { CameraLayer } from "./layers/cameras.js";
import { LandUseLayer } from "./layers/landuse.js";
import { StreetWorkLayer } from "./layers/streetwork.js";
import { EventLayer } from "./layers/events.js";
import {
  DOOR_MIN_ZOOM, doorCandidates, EntranceLayer, inferredDoorLayer,
  inferredDoors,
} from "./layers/entrances.js";
import { BarrierLayer } from "./layers/barriers.js";
import { YouAreHere } from "./layers/you.js";
import { PoliceLayer } from "./layers/police.js";
import { CotripLayer } from "./layers/cotrip.js";
import { computeViewsheds, viewshedLayer } from "./layers/viewshed.js";
import { BuildingEdits } from "./layers/building-edits.js";
import { MarkStore, scanPulseLayer } from "./layers/marks.js";
import { mountHud } from "./ui/hud.js";
import { mountTooltip } from "./ui/tooltip.js";
import { createCompass } from "./ui/heading.js";
import { createFollow } from "./ui/follow.js";
import { mountSettings } from "./ui/settings.js";
import { mountCompass, mountViewport } from "./ui/viewport.js";
import { DWELL_MS, mountScan } from "./ui/scan.js";
import { mountScanCard } from "./ui/scan-card.js";
import { mountWatch } from "./ui/watch.js";
import { mountWalk } from "./ui/walk.js";
import { createRouting } from "./ui/route.js";

/** How close counts as arrived, in metres. About a building's frontage. */
const ARRIVAL_M = 30;

/** Beacon breathing period. */
const PULSE_MS = 3200;

const el = (id) => document.getElementById(id);

function lighting() {
  return new LightingEffect({
    ambient: new AmbientLight({ color: [255, 255, 255], intensity: 0.85 }),
    sun: new DirectionalLight({
      color: [255, 255, 255],
      intensity: 1.4,
      direction: [-2, -6, -3],
    }),
  });
}

async function boot() {
  // Before the map is built, so the first style is drawn in the right palette.
  initTheme();

  const bootStatus = el("boot-status");

  const BUILDING_TILES = "./data/buildings.pmtiles";
  const { map, hasBasemap } = await createMap("map");

  const overlay = new MapboxOverlay({
    interleaved: true,
    effects: [lighting()],
    layers: [],
    // Picking runs inside deck's own render loop rather than being driven
    // from a mousemove handler; see map/picking.js.
    pickingRadius: 8,
    onHover: recordHover,
    onClick: recordClick,
  });
  map.addControl(overlay);

  // ── City geometry ──────────────────────────────────────────────────────
  bootStatus.textContent = "LOADING CITY GEOMETRY…";
  // Tiles when the archive is there, the whole file when it is not. Either
  // way the footprints land in the shared index, so nothing downstream has to
  // know which happened.
  const buildingsTiled = await archiveExists(BUILDING_TILES);
  let polygons = [];
  let metadata = null;

  if (!buildingsTiled) {
    const loaded = await loadBuildings();
    metadata = loaded.metadata;
    polygons = toPolygons(loaded.features);
    footprints.add("file", polygons);
  }
  let buildingsVisible = true;

  // ── Layers ─────────────────────────────────────────────────────────────
  bootStatus.textContent = "READING SOURCES…";
  const incidents = new IncidentLayer({ basePath: "./api" });
  const cameras = new CameraLayer({ basePath: "./api" });
  const landuse = new LandUseLayer({ basePath: "./api" });
  const streetwork = new StreetWorkLayer({ basePath: "./api" });
  const events = new EventLayer({ basePath: "./api" });
  const entrances = new EntranceLayer({ basePath: "./api" });
  const police = new PoliceLayer({ basePath: "./data" });
  const barriers = new BarrierLayer({ basePath: "./api" });
  const cotrip = new CotripLayer({ basePath: "./api" });
  // Corrections people have made to building records. Loaded alongside the
  // rest; the map is fine without them.
  const you = new YouAreHere();
  // Routing draws over everything, so it composes with the frame rather than
  // living in the layer list.
  const routing = createRouting({
    apiBase: "./api",
    onChange: () => { draw(); paintRoute(); },
    // Read live rather than captured: the dot moves as the fix improves.
    locate: () => you.position,
  });
  // Viewsheds are geometry, not a feed: computed here from footprints already
  // loaded, for the cameras currently on screen.
  let viewshedOn = false;
  let sheds = [];
  // Doors synthesised from footprint geometry for the 95% of buildings OSM
  // has no entrance for. Recomputed on move, only when zoomed in enough.
  let doors = [];
  // Scan runs at its own tempo, faster than the beacon breath.
  let scanTarget = null;

  // Constructed here but loaded off the boot path: the corrections are "an
  // addition to the map, never a dependency of it", so awaiting the
  // /api/contributions fetch before first paint spent a round-trip holding the
  // map on its loading screen for data that shows up empty-safe. It streams in
  // below alongside places, and refreshShading repaints once it lands — which
  // is when the police-tinted buildings appear anyway.
  const buildingEdits = new BuildingEdits({ apiBase: "./api" });
  // Land use first so parks and lots sit beneath everything else.
  const layers = [
    landuse, streetwork, cotrip, barriers, entrances, cameras, police, events,
    incidents,
  ];
  const marks = new MarkStore({ apiBase: "./api" });
  const places = new PlaceIndex();

  // What the first paint actually needs, and nothing else.
  //
  // This used to await every layer, which meant waiting on the 5.8MB land use
  // payload before a single building appeared — roughly 10MB of blocking for a
  // map that is legible after 3. Incidents are the point of the board and
  // marks are yours, so those two are worth waiting for; everything else
  // arrives on its own schedule and repaints as it lands.
  // Tiles are optional: if the archive has not been built the layer falls back
  // to the API payload, so a deploy that forgets them still works.
  // One round-trip, not two: these two archive probes are independent, so
  // asking in parallel takes a single request time off the path to first paint.
  [landuse.tiled, barriers.tiled] = await Promise.all([
    archiveExists(landuse.tileUrl),
    archiveExists(barriers.tileUrl),
  ]);
  function anchorLandUse({ repaint = true } = {}) {
    // Only name a style layer that is really there. It is absent when there is
    // no basemap archive, and absent again while setStyle swaps themes —
    // pointing the overlay at a layer that does not exist is not something to
    // discover at render time.
    const next = map.getLayer?.("roads-service") ? "roads-service" : undefined;
    if (next === landuse.beforeId) return;
    landuse.beforeId = next;
    if (repaint) paint();
  }

  // Nothing has been drawn yet, so there is nothing to repaint — and paint()
  // reads a dozen bindings that this line is above. Repainting here is what
  // failed as "cannot access 'Li' before initialization", and only sometimes:
  // the anchor acts only when it finds `roads-service`, so it threw when the
  // basemap style had loaded by this point and did nothing when it had not,
  // which is a race that survives every test and then breaks for a user.
  anchorLandUse({ repaint: false });

  // setStyle is asynchronous, so a theme swap has to re-anchor once the new
  // style is actually in place rather than immediately after asking for it.
  // By then everything is drawn, so this one does repaint.
  map.on("styledata", () => anchorLandUse());
  const deferred = layers.filter((l) => l !== incidents);

  await Promise.all([incidents.load(), marks.load()]);

  let hudRef = null;

  /** Repaint as each late layer arrives, rather than once at the end. */
  function streamIn(promise, after) {
    return promise.then(() => {
      after?.();
      // hud may not be mounted yet for the earliest arrivals.
      paint();
      hudRef?.tick();
    }).catch(() => { /* a layer that fails is already marked unavailable */ });
  }

  /**
   * The live cursor.
   *
   * Anchored to the newest record rather than wall-clock now, because Denver's
   * reporting pipeline lags by days — pinning to now() would show an empty map
   * and read as broken. The lag is displayed rather than hidden.
   */
  let now = incidents.latest ?? Date.now();

  // ── Render ─────────────────────────────────────────────────────────────
  let pulse = 0;
  const SCAN_PULSE_MS = 1400;
  /**
   * Viewsheds appear only once you are properly down at street level.
   *
   * Higher than it looks like it should be, on purpose. At 105m reach the
   * shapes from neighbouring cameras merge into one wash well before they stop
   * being individually legible, so the useful zoom is closer in than the zoom
   * at which they would first be visible.
   */
  const VIEWSHED_MIN_ZOOM = 16.2;

  // The beacons animate every frame; nothing else does. Rebuilding the whole
  // stack at 60fps meant reconstructing the 23k-area land use layer and 1.6k
  // buildings on every tick, which cost more time than a frame has — the
  // pulse stuttered because the renderer could not keep up with it.
  //
  // deck.gl diffs by layer identity, so handing back the *same* instances for
  // the unchanged layers makes it skip them entirely. Only the marks are
  // rebuilt per frame.
  let staticLayers = [];

  // Per-feature now rather than two passes over the whole city: tiles arrive
  // after any such pass would have run, so the questions are asked at draw
  // time. Both were always lookups, so nothing got slower.
  let isKnown = () => true;
  let tintAt = () => null;
  // Bumped whenever the two above are rebuilt, so the buildings sublayer knows
  // its colour buffer is describing yesterday's answers (see buildingTileLayer).
  let shadingStamp = 0;

  /**
   * The static stack, rebuilt a slot at a time rather than all at once.
   *
   * This built everything on every call: a fresh buildings TileLayer, all nine
   * feed layers, the doors and the viewsheds. Most of a pan changes none of
   * that — the doors and the cones are recomputed, and the other two slots are
   * reconstructed identically for nothing. Since paint() runs at the end of
   * every gesture, that full rebuild was the spike left after the viewsheds
   * were sliced.
   *
   * Each slot is cached and rebuilt only when something invalidates it. The
   * default is still to rebuild everything, so a caller that has not thought
   * about it gets the old behaviour rather than a stale map.
   */
  const slots = { buildings: null, feeds: null, entrances: null, doors: null, sheds: null };

  // The zoom the feeds slot was last built at. Some feed layers change what
  // they render across a zoom threshold (see Layer.zoomThresholds); the
  // moveend path compares against this to know whether the cached slot is
  // telling yesterday's truth.
  let feedsZoom = null;

  function rebuildStatic({ only } = {}) {
    const wants = (name) => !only || only.includes(name);

    if (wants("buildings") || !slots.buildings) {
      slots.buildings = buildingsTiled
        ? buildingTileLayer({
            url: BUILDING_TILES,
            visible: buildingsVisible,
            isKnown,
            tintAt,
            shadingStamp,
            // A tile arriving changes what the rooftop lift and the doors can
            // see. Through the sliced path, not a synchronous pass: this fires
            // once per tile, tiles land in bursts right after a pan, and the
            // full-viewport door walk is a third of a second at downtown
            // density — running it inline here was the post-pan hitch that
            // survived slicing the moveend path. (The old inline pass then
            // called draw(), which composes CACHED slots — the recomputed
            // doors were not even drawn until the next repaint.)
            onChange: () => refreshHeavy(),
          })
        : buildingLayer(polygons, { visible: buildingsVisible });
    }

    if (wants("feeds") || !slots.feeds) {
      // Entrances are excluded here and rebuilt on the per-pan path below: their
      // geometry is viewport-culled, so it depends on where you are looking,
      // which the cached feeds slot deliberately does not track.
      feedsZoom = map.getZoom();
      slots.feeds = layers
        .filter((layer) => layer !== entrances)
        .flatMap((layer) => layer.render(now, feedsZoom));
    }

    // Mapped doors for the current viewport. Rebuilt on pan alongside the
    // inferred doors and the viewsheds, and empty below the door zoom.
    if (wants("entrances") || !slots.entrances) {
      const eb = map.getBounds();
      slots.entrances = entrances.renderInView(map.getZoom(),
        [[eb.getWest(), eb.getSouth()], [eb.getEast(), eb.getNorth()]]);
    }

    if (wants("doors") || !slots.doors) {
      slots.doors = [inferredDoorLayer(doors, {
        visible: entrances.enabled && doors.length > 0,
      })];
    }

    if (wants("sheds") || !slots.sheds) {
      slots.sheds = viewshedOn
        ? [viewshedLayer(sheds, { zoom: map.getZoom() })]
        : [];
    }

    staticLayers = [slots.buildings, ...slots.feeds, ...slots.entrances,
      ...slots.doors, ...slots.sheds];
  }

  /** Compose the frame. Cheap: only the beacon layers are new each call. */
  function draw() {
    const fast = (Date.now() % SCAN_PULSE_MS) / SCAN_PULSE_MS;
    overlay.setProps({
      layers: [
        ...staticLayers,
        ...marks.render(pulse),
        ...scanPulseLayer(scanTarget, fast),
        ...you.render(pulse),
        ...routing.layers(),
      ],
    });
  }

  /** Something other than the pulse changed — rebuild everything, then draw. */
  function paint(options) {
    rebuildStatic(options);
    draw();
  }

  // Deliberately not repainting on "zoom". That fired on every frame of a
  // zoom gesture and rebuilt the entire static stack each time — 23,017 land
  // use polygons and 43,071 buildings reconstructed per frame, which is what
  // made greenery, parking and the viewsheds flicker while the map moved and
  // settle the instant it stopped. Nothing in the static stack depends on
  // zoom any more; the layers that do refresh on moveend.

  // The rooftop lift reads the shared index, which fills as tiles load.
  marks.groundHeight = makeGroundHeight();
  streetwork.groundHeight = (d) => footprints.heightAt({ lng: d.lng, lat: d.lat });
  events.groundHeight = (d) => footprints.heightAt({ lng: d.lng, lat: d.lat });
  // Incidents are points in x/y rather than lng/lat, and were the one
  // marker layer never given a height to stand on.
  incidents.groundHeight = (d) => footprints.heightAt({ lng: d.x, lat: d.y });

  /** Recompute the on-screen viewsheds. Cheap enough to run on move-end. */

  /** Nearest-mapped-entrance test, so inference never covers a surveyed door. */
  // Backed by the layer's grid index: a flat scan over 30k entrances, once per
  // footprint in view, was tens of millions of comparisons a pan.
  const mappedNearby = (lng, lat) => entrances.hasNear(lng, lat);

  function refreshViewsheds() {
    // All or nothing, past a zoom. Computing whatever fits under a cap meant
    // a handful appeared, then more arrived as you zoomed, which reads as the
    // map struggling rather than as a deliberate level of detail. Below the
    // threshold there are more cameras on screen than the shapes could be
    // told apart at anyway.
    if (!viewshedOn || map.getZoom() < VIEWSHED_MIN_ZOOM) { sheds = []; return; }
    const b = map.getBounds();
    sheds = computeViewsheds(
      cameras.allCameras?.() ?? [],
      footprints,
      [[b.getWest(), b.getSouth()], [b.getEast(), b.getNorth()]],
      { limit: Infinity },
    );
  }

  /**
   * The expensive part of a move, done after the move rather than during it.
   *
   * Viewsheds march 72 rays out of every camera on screen, testing each step
   * against the footprint index, and inferred doors walk every footprint in
   * view. Both ran synchronously inside the moveend handler, so the frame that
   * ended a pan also paid for all of it — the map stopped dead at the end of
   * every gesture, and worst with camera view on, which is exactly when the
   * viewshed work exists.
   *
   * Deferred to an idle callback they land a frame or two later and the gesture
   * finishes clean. Any further move cancels the pending one, so dragging
   * across the city computes this once at the end rather than once per pause.
   */
  let cancelHeavy = null;

  /**
   * Everything expensive a move implies, spread over frames.
   *
   * Deferring this to an idle callback stopped it stalling the gesture, but it
   * still arrived as one task of a few hundred milliseconds — the pan ran clean
   * and then the map hitched once, which is the same lost frames moved
   * somewhere else. It is now a queue of small pieces with a budget per frame,
   * so the browser draws in between and nothing blocks for longer than that.
   *
   * Viewsheds are split by camera because that is where the cost is: 72 rays
   * each, every step tested against the footprint index. Six at a time keeps
   * each piece well inside the budget, and painting as they accumulate means
   * the cones fill in rather than appearing all at once after a wait.
   */
  const VIEWSHED_BATCH = 6;

  function refreshHeavy() {
    cancelHeavy?.();

    const tasks = [];

    // Doors, in pieces. One pass over every footprint in view is a third of a
    // second at downtown density, and that was the whole of the hitch left
    // after the viewsheds were sliced.
    const DOOR_BATCH = 600;
    if (map.getZoom() >= DOOR_MIN_ZOOM && entrances.enabled) {
      const db = map.getBounds();
      const doorBounds = [
        [db.getWest(), db.getSouth()], [db.getEast(), db.getNorth()],
      ];
      const candidates = doorCandidates(footprints);
      doors = [];
      for (const batch of chunk(candidates, DOOR_BATCH)) {
        tasks.push(() => {
          doors = doors.concat(
            inferredDoors(footprints, doorBounds, mappedNearby, { rows: batch }),
          );
        });
      }
    } else {
      doors = [];
    }

    if (viewshedOn && map.getZoom() >= VIEWSHED_MIN_ZOOM) {
      const b = map.getBounds();
      const bounds = [[b.getWest(), b.getSouth()], [b.getEast(), b.getNorth()]];
      const all = cameras.allCameras?.() ?? [];
      sheds = [];
      for (const batch of chunk(all, VIEWSHED_BATCH)) {
        tasks.push(() => {
          sheds = sheds.concat(
            computeViewsheds(batch, footprints, bounds, { limit: Infinity }),
          );
        });
      }
    } else {
      sheds = [];
    }

    cancelHeavy = runSliced(tasks, (task) => task(), {
      // Painted once, at the end.
      //
      // The first version repainted on every slice, to fill the cones in as
      // they were computed. paint() rebuilds the whole static stack, so that
      // put a full rebuild inside every frame of the very loop meant to keep
      // frames cheap — measurably worse than the single long task it replaced.
      // Filling in progressively is not worth that.
      onDone: () => {
        cancelHeavy = null;
        // Only what this path changed. The buildings layer and the feed layers
        // are identical to what is already on screen, and rebuilding them was
        // the rest of the spike. The mapped doors move with the viewport, so
        // they refresh here alongside the inferred ones.
        //
        // One exception: a feed layer whose output changes across a declared
        // zoom threshold (the land use greenery gate, the barriers fallback).
        // If this move ended on the other side of one, the cached feeds slot
        // is wrong until the next full paint — which can be the one-minute
        // refresh tick, and greenery a minute late reads as greenery missing.
        // Rebuilding feeds only on a crossing keeps the usual move as cheap
        // as before.
        const only = ["doors", "sheds", "entrances"];
        const zoom = map.getZoom();
        const crossed = feedsZoom != null && layers.some((layer) =>
          layer.zoomThresholds.some((t) => (feedsZoom >= t) !== (zoom >= t)));
        if (crossed) only.push("feeds");
        paint({ only });
      },
    });
  }

  // A new gesture abandons the previous answer: it was about a viewport nobody
  // is looking at any more.
  map.on("movestart", () => { cancelHeavy?.(); cancelHeavy = null; });
  map.on("moveend", refreshHeavy);

  const hud = mountHud({
    layers,
    marks,
    map,
    getNow: () => now,
    latest: incidents.latest ?? null,
    serverNow: incidents.serverNow ?? Date.now(),
    buildings: {
      count: () => footprints.count,
      metadata,
      isVisible: () => buildingsVisible,
      toggle: (on) => { buildingsVisible = on; paint(); },
      viewshedOn: () => viewshedOn,
      toggleViewshed: (on) => {
        viewshedOn = on;
        refreshViewsheds();
        paint();
      },
    },
    buildingEdits,
    onChange: () => {
      // A building edit can change how that building is drawn, so the shading
      // predicates are rebuilt before repainting.
      refreshShading();
    },
  });

  hudRef = hud;

  /** Rebuild the shading predicates and redraw. */
  function refreshShading() {
    isKnown = makeIsKnown(places, buildingEdits);
    tintAt = makeTintAt(buildingEdits, police._stations ?? []);
    shadingStamp += 1;
    paint();
  }

  // The rest of the city, streamed. Land use is by far the largest and is
  // pure context, so it is deliberately last in the queue.
  for (const layer of deferred) {
    // Police stations feed the building tint, so its arrival changes shading.
    streamIn(layer.load(), layer === police ? refreshShading : undefined);
  }
  // Place names feed the "is anything known about this building" pass, so that
  // has to run again once they arrive.
  streamIn(places.load(), refreshShading);
  // Building corrections, moved off the boot await above: same treatment as
  // places — repaint when they land, and the map is complete without them.
  streamIn(buildingEdits.load(), refreshShading);

  const viewport = mountViewport({ onChange: () => map.resize() });
  mountCompass(map);
  // Arrow keys walk the camera along its bearing. Inert without a keyboard,
  // which is the whole of what "desktop only" needs to mean here.
  mountWalk({ map });

  // A lost WebGL context blanks the canvas with no message anywhere. It is
  // rare, but "the screen went black" is impossible to act on, and the browser
  // will tell us if we ask.
  map.getCanvas()?.addEventListener("webglcontextlost", (event) => {
    event.preventDefault();
    el("status").textContent = "GRAPHICS LOST // RELOAD";
    console.error("WebGL context lost — the map cannot draw until reloaded.");
  });
  map.getCanvas()?.addEventListener("webglcontextrestored", () => {
    el("status").textContent = "LIVE";
  });

  // ── Routing ─────────────────────────────────────────────────────────────
  // Mobile only. Routing is a thing you do while standing in the city with a
  // phone in your hand; at a desk it is a worse version of a map you already
  // have open, and it was spending a top-bar slot to say so.
  const routePanel = el("route-panel");
  const routeGo = el("route-go");

  // Travel mode in the sheet as well as in settings. Settings is where you set
  // a preference once; this is where you change your mind halfway down a
  // street, and on a phone that should not be a trip back through a menu.
  const routeModes = el("route-modes");
  for (const mode of routing.modes) {
    const button = document.createElement("button");
    button.type = "button";
    button.textContent = mode.label;
    button.dataset.mode = mode.key;
    button.addEventListener("click", () => { routing.setMode(mode.key); paintRoute(); });
    routeModes?.append(button);
  }

  let follow = null;

  /**
   * Heading-up: the map turns as you do.
   *
   * Off by default, because a map that rotates while you are reading it is
   * disorienting when you are not walking. On, it is the difference between
   * translating every turn in your head and just looking — left on screen is
   * left in front of you.
   */
  let headingUp = false;

  const compass = createCompass({
    onChange: (heading) => {
      // The wedge on the dot tracks whether or not the map does. Which way you
      // are pointed is the other half of "where am I", and it costs nothing to
      // answer even with the map held north up.
      you.heading = heading;
      draw();
      follow?.onHeading(you.position);
      // While following, the follow camera already owns the bearing. Two things
      // easing it at once fight, and the map judders.
      if (headingUp && !follow?.active) {
        map.easeTo({ bearing: heading, duration: 260, essential: true });
      }
    },
  });

  /**
   * The compass button turns heading-up on and off, on touch.
   *
   * It is the right control for it: it already says which way the map faces,
   * and "turn with me" is the same question as "which way is north" asked from
   * the other side. A desktop keeps only its original job, having no device
   * heading to follow.
   *
   * The tap is also what iOS requires before handing over the magnetometer, so
   * this is the one place asking for it cannot surprise anybody.
   */
  if (window.matchMedia("(pointer: coarse)").matches) {
    const compassButton = el("compass");
    compassButton?.addEventListener("click", async () => {
      headingUp = !headingUp;
      compassButton.setAttribute("aria-pressed", String(headingUp));
      compassButton.title = headingUp
        ? "Turning with you — tap to hold north up"
        : "Face north (N)";
      if (!headingUp) return;
      await compass.start();
      if (Number.isFinite(compass.heading)) {
        map.easeTo({ bearing: compass.heading, duration: 300, essential: true });
      }
      // Turning it off needs nothing: mountCompass's own handler has already
      // run and put the map back to north.
    });
  }

  // A phone is locked flat, so the following camera has to be too — asking for
  // 60 degrees against a maxPitch of 0 leaves the camera arguing with itself on
  // every fix.
  const FLAT = window.matchMedia("(pointer: coarse)").matches;

  follow = createFollow({
    map,
    compass,
    pitch: FLAT ? 0 : undefined,
    onChange: (active) => {
      routeGo.setAttribute("aria-pressed", String(active));
      routeGo.textContent = active ? "Stop" : "Start";
      paintRoute();
    },
  });

  /** Reflect routing state into the panel. */
  function paintRoute() {
    const { state } = routing;
    const mode = routing.modes.find((m) => m.key === state.mode);
    el("route-mode-hint").textContent = mode ? `· ${mode.label.toLowerCase()}` : "";
    for (const button of routeModes?.children ?? []) {
      button.setAttribute("aria-pressed", String(button.dataset.mode === state.mode));
    }

    el("route-to").setAttribute("aria-pressed", String(state.picking === "to"));
    el("route-to").textContent = state.to ? "Change destination" : "Set destination";

    // Nothing to start until there is a route to follow.
    routeGo.hidden = !state.summary;

    const summary = el("route-summary");
    if (state.busy) summary.textContent = "Routing…";
    else if (state.arrived) summary.textContent = "Arrived.";
    else if (state.summary) {
      summary.textContent = follow.active
        ? `${state.summary.distance} · ${state.summary.duration} · following you`
        : `${state.summary.distance} · ${state.summary.duration}`;
    } else if (state.picking) {
      summary.textContent = "Tap the map to set your destination.";
    } else if (!you.position) {
      summary.textContent = "Waiting for your location…";
    } else {
      summary.textContent = "Pick a destination on the map.";
    }

    const error = el("route-error");
    error.textContent = state.error ?? "";
    error.hidden = !state.error;

    // While you are choosing a destination the sheet gets out of the way.
    //
    // It is a bottom sheet on a phone, and asking someone to pan the map to
    // find somewhere while it covers a third of that map is asking them to
    // look through it. Collapsed, it is one line saying what it waits for.
    routePanel.classList.toggle(
      "is-picking", state.picking === "to" && !state.summary,
    );
  }

  el("route-to").addEventListener("click", () => routing.pick());
  el("route-clear").addEventListener("click", () => { follow.stop(); routing.clear(); });
  routeGo.addEventListener("click", () => {
    follow.toggle(you.position);
    // Starting is the point at which the map becomes the thing you are using,
    // so the panels get out of the way. Not the whole interface — the tools
    // panel is how a mark or a camera gets placed, and someone navigating
    // somewhere is exactly who wants to mark what they find when they arrive.
    // Stowed, not hidden: one tap on a caret brings it back.
    //
    // The route sheet closes too — it covers a third of the map it just asked
    // you to follow. Hidden directly rather than through showRoute(false),
    // which would clear the route it exists to show; the route toggle brings
    // it back with the Stop button on it, and a drag on the map also stops
    // the follow without needing the sheet at all.
    if (follow.active) {
      viewport.stowPanels();
      routePanel.hidden = true;
      el("toggle-route-mobile")?.setAttribute("aria-pressed", "false");
    }
  });

  function showRoute(next) {
    routePanel.hidden = !next;
    el("toggle-route-mobile")?.setAttribute("aria-pressed", String(next));
    if (!next) { follow.stop(); routing.clear(); return; }

    // Opening the sheet gets the panels out of the way and arms the map in one
    // go. The tools panel this button lives on covers most of a phone, so
    // asking for a destination while it is still open is asking someone to tap
    // a map they cannot see — and making them press "Set destination" first is
    // a second tap for something they have already said they want.
    viewport.stowPanels();
    if (!routing.state.picking && !routing.state.to) routing.pick();
    paintRoute();
  }
  el("route-close").addEventListener("click", () => showRoute(false));
  el("toggle-route-mobile")?.addEventListener("click", () => showRoute(routePanel.hidden));
  paintRoute();

  // Routing takes a click before anything else can, but only while armed —
  // and only from a real tap.
  //
  // A pan across the city looking for somewhere, and a pinch to see it better,
  // both end in a click that MapLibre reports exactly like a tap. Both used to
  // drop a destination wherever the finger happened to lift.
  const wasTap = watchTaps(map);
  window.addEventListener("pulse:map-click", (event) => {
    if (!wasTap()) return;
    if (routing.placeAt(event.detail)) event.detail.claim?.();
  });

  // ── Settings ────────────────────────────────────────────────────────────
  const settings = mountSettings({
    modes: routing.modes,
    getMode: () => routing.state.mode,
    setMode: (mode) => routing.setMode(mode),
    setChromeHidden: viewport.setChromeHidden,
    chromeHidden: viewport.chromeHidden,
    onChange: paintRoute,
  });

  // Theme. The basemap is rebuilt from the new palette; the deck layers read
  // it at render time, so they only need a repaint — but the cached icon
  // bitmaps key on the theme name and regenerate themselves.
  watchTheme(map);
  onThemeChange(() => {
    paint();
    hud.tick();
    // The legend is drawn from the layers' colours, so it has to be rebuilt
    // rather than merely refreshed — tick() only updates counts.
    hud.paintLegend();
    settings.refresh();
  });
  const scanCard = mountScanCard({ dwellMs: DWELL_MS });
  let chromeWasHidden = false;

  const scan = mountScan({
    map, layers, getNow: () => now,
    onChange: () => hud.tick(),
    // Scan takes over the screen: the board becomes the city and the one card
    // reporting what it found. Whatever the interface was before is restored on
    // stop, so someone who had already hidden it does not get it handed back.
    onRunChange: (running) => {
      if (running) {
        chromeWasHidden = viewport.chromeHidden();
        viewport.setChromeHidden(true);
        viewport.setFullscreen(true);
      } else {
        viewport.setChromeHidden(chromeWasHidden);
        viewport.setFullscreen(false);
      }
    },
    onTarget: (row, position) => {
      scanTarget = row;
      // The incident layer already knows how to describe one of its own rows —
      // the same text the hover tooltip shows. The tour just never asked for it.
      scanCard.show(row ? incidents.describe(row) : null, position);
    },
  });

  // Leaving fullscreen ends the tour.
  //
  // Escape is what everyone reaches for, and the browser handles it before any
  // of this code sees it: fullscreen closes and the scan carries on with the
  // interface still hidden and the button that would stop it out of sight. So
  // leaving fullscreen is treated as the stop, which makes Escape the exit for
  // the whole thing rather than half of it.
  document.addEventListener("fullscreenchange", () => {
    if (!viewport.isFullscreen() && scan.running()) scan.stop();
  });

  // The card is the one thing left on screen during a scan, so it is also a
  // way out — clicking it stops the tour.
  el("scan-card")?.addEventListener("click", () => {
    if (scan.running()) scan.stop();
  });

  // Your own position, tracked continuously so it settles as the fix improves.
  you.start((position, coords) => {
    draw();
    compass.fromPosition(coords);
    follow.onPosition(position);

    // Arriving is the one thing that ends a route on its own. Everything else
    // — panning, zooming, the screen locking — leaves the line where it is,
    // because the line answers a question nobody has withdrawn.
    const target = routing.state.to;
    if (follow.active && target) {
      const dx = (position[0] - target[0]) * 85600;
      const dy = (position[1] - target[1]) * 110540;
      if (Math.hypot(dx, dy) < ARRIVAL_M) {
        follow.stop();
        routing.arrive();
      }
    }
    // The panel says "waiting for your location" until there is one.
    if (!routePanel.hidden) paintRoute();
  });
  /**
   * The police station standing on a point, if there is one.
   *
   * Same test the red tint uses — containment first, then a 45m fallback for a
   * station mapped as a point on the kerb — so a building that looks like a
   * police building and a building that reports itself as one can never
   * disagree.
   */
  const policeAt = ([lng, lat]) => {
    for (const station of police._stations ?? []) {
      if (!Number.isFinite(station.lng) || !Number.isFinite(station.lat)) continue;
      const dx = (lng - station.lng) * 85600;
      const dy = (lat - station.lat) * 110540;
      if (Math.hypot(dx, dy) < 45) return station;
    }
    return null;
  };

  mountTooltip({ map, overlay, layers, marks, places, buildingEdits, you, policeAt });
  // Clicking a live feed opens its player; the tooltip dispatches the event.
  mountWatch();

  // ── Live loops ─────────────────────────────────────────────────────────
  // Beacons breathe on their own clock so marks stay eye-catching on a wall
  // display without anything else re-rendering.
  /**
   * The beacon breath, rebuilt far less often than once a frame.
   *
   * This ran draw() on every animation frame, which rebuilt the whole dynamic
   * layer stack — marks, the scan pulse, the location dot, the route — sixty
   * times a second, forever, whether or not anything was animating. deck then
   * re-uploaded those attributes on each one. That is the pan lag: the map was
   * competing with a full layer rebuild for every frame it wanted to draw.
   *
   * Two things fix it and neither costs anything visible. The breath is a
   * 3.2-second cycle, so twelve steps a second is smooth and 60 was five times
   * more than the eye is given. And when nothing is pulsing there is no reason
   * to redraw at all — the static stack is already on screen and unchanged.
   */
  const PULSE_FPS = 12;
  const PULSE_FRAME_MS = 1000 / PULSE_FPS;
  let lastPulseDraw = 0;

  const tickPulse = () => {
    requestAnimationFrame(tickPulse);

    const now = Date.now();
    if (now - lastPulseDraw < PULSE_FRAME_MS) return;

    // Marks breathe, the scan target pulses, the location dot sweeps. With none
    // of those on screen the frame would be identical to the last one.
    const animating = marks.count > 0 || scanTarget || you.position
      || routing.state.path.length > 1;
    if (!animating) return;

    lastPulseDraw = now;
    pulse = (now % PULSE_MS) / PULSE_MS;
    draw();
  };
  tickPulse();

  // When each polled layer was last re-read. Seeded at boot, because it has
  // just been loaded and asking again a minute later would be asking twice
  // for the same bytes.
  const lastRead = new Map(layers.map((layer) => [layer, Date.now()]));

  /**
   * Each layer's own jitter, as a fraction of its interval.
   *
   * Without this every tab that booted together stays together, and a hundred
   * of them ask for the same thing in the same second forever — the load is
   * identical in total but arrives in spikes, and the spikes are what falls
   * over. Fixed per layer rather than re-rolled each time, so a layer keeps a
   * steady cadence instead of drifting.
   */
  const jitter = new Map(layers.map((layer) => [layer, Math.random() * 0.2 - 0.1]));

  /**
   * Re-read the sources that have gone stale; pick up anything newly published.
   *
   * Only layers declaring `refreshMs` are polled, and only when their own
   * interval has elapsed. Most declare nothing: re-reading the 6.1 MB land use
   * polygons — which the server caches for a week — would cost every viewer a
   * download the size of the rest of the map put together, to learn nothing.
   */
  async function refresh({ force = false } = {}) {
    // A hidden tab is not being read. Polling it spends the viewer's data and
    // our bandwidth to update pixels nobody is looking at; visibilitychange
    // below catches it up the moment it comes forward.
    if (document.hidden && !force) return;

    const at = Date.now();
    const due = layers.filter((layer) => {
      const every = layer.constructor.refreshMs;
      if (!every) return false;
      if (force) return true;
      return at - (lastRead.get(layer) ?? 0) >= every * (1 + jitter.get(layer));
    });
    if (due.length === 0) return;

    const results = await Promise.all(due.map(async (layer) => {
      lastRead.set(layer, at);
      // load() marks its own layer unavailable and resolves; this guards the
      // rest of the batch against one that throws outright.
      try {
        return await layer.load();
      } catch {
        return null;
      }
    }));

    // The incident feed carries the clock: its newest record is what "now"
    // means on this map, so a refresh that finds fresher data moves the cursor.
    for (const result of results) {
      if (result?.latest && result.latest > now) now = result.latest;
    }
    paint();
    hud.tick();
  }

  // One ticker, not one per layer: it wakes each minute and asks who is due,
  // which keeps the timers from multiplying as layers are added.
  setInterval(refresh, 60 * 1000);
  // Catch up when a backgrounded tab comes forward — but only what is actually
  // stale. A tab switched to and from all afternoon used to refetch every time.
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) refresh();
  });

  // Assigned before the guard installs: the guard writes the failure text here
  // for diagnosis, and a shader can fail on the very first paint.
  window.__pulse = {
    map, layers, marks, places, paint, refresh,
    getNow: () => now,
    doorCount: () => doors.length,
  };

  // If the facade shader fails to compile on this driver, drop it and carry on
  // rather than leaving a wall of GLSL over a blank map.
  // Kept even though the facade shader is gone: any future shader failure
  // should degrade quietly rather than paint GLSL over the city.
  watchForShaderErrors((text) => {
    console.warn("shader failed to compile:", text);
    window.__pulse.lastShaderError = text;
    paint();
  });

  paint();
  hud.tick();

  el("status").textContent = hasBasemap ? "LIVE" : "LIVE // NO BASEMAP";
  const boot = el("boot");
  boot.classList.add("fading");
  setTimeout(() => { boot.hidden = true; }, 400);

  // Asked for only once the map is drawn and usable. The permission prompt
  // then interrupts something that already works, rather than a loading
  // screen — and a viewer who ignores it never notices.
  locateViewer(map);

}

boot().catch((error) => {
  console.error(error);

  // Show where it came from, not just what it said.
  //
  // The message alone has twice been a minified variable name, which changes
  // every build and identifies nothing. The top frames are on screen because
  // whoever hits this is looking at the page, not at a console.
  const status = document.getElementById("boot-status");
  if (!status) return;

  const frames = String(error?.stack ?? "")
    .split(/\r?\n/)
    .slice(1, 4)
    .map((line) => line.trim().replace(/^at\s+/, ""))
    .filter(Boolean);

  status.textContent = `FAILED: ${error.message}`;
  if (!frames.length) return;

  const where = document.createElement("div");
  where.className = "label";
  where.style.cssText = "margin-top:8px; opacity:0.75; font-size:11px;"
    + "max-width:80vw; overflow-wrap:anywhere; text-align:left;";
  where.textContent = frames.join(String.fromCharCode(10));
  where.style.whiteSpace = "pre-wrap";
  status.after(where);
});
